import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
  useCallback
} from "react";
import Drawer from "react-modern-drawer";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  ButtonVariant,
  ButtonSize,
  Show,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import queryString from "qs";
import {
  CreatedWithinOption,
  LeadsFilters,
  ShowingWithinOption
} from "../../types";
import { instance } from "../../../../../api/apiBase";
import { FilterSection } from "./FilterSection";
import { Cancel01Icon } from "@hugeicons-pro/core-solid-rounded";
import {
  CalendarMinus02Icon,
  Calendar03Icon,
  CalendarCheckIn02Icon,
  CalendarCheckOut02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  DateRangeSelect,
  DateRangePreset
} from "shared/components/DateRangeSelect";

const LEADS_DATE_PRESETS: DateRangePreset[] = [
  {
    value: CreatedWithinOption.None,
    label: "Don't filter",
    icon: CalendarMinus02Icon,
    isNone: true
  },
  {
    value: CreatedWithinOption.Last7Days,
    label: "Last 7 days",
    icon: CalendarCheckIn02Icon
  },
  {
    value: CreatedWithinOption.Last30Days,
    label: "Last 30 days",
    icon: CalendarCheckIn02Icon
  },
  {
    value: CreatedWithinOption.Custom,
    label: "Pick a date range",
    icon: Calendar03Icon,
    isCustom: true
  }
];

const SHOWING_DATE_PRESETS: DateRangePreset[] = [
  {
    value: ShowingWithinOption.None,
    label: "Don't filter",
    icon: CalendarMinus02Icon,
    isNone: true
  },
  {
    value: ShowingWithinOption.Last7Days,
    label: "Last 7 days",
    icon: CalendarCheckIn02Icon
  },
  {
    value: ShowingWithinOption.Last30Days,
    label: "Last 30 days",
    icon: CalendarCheckIn02Icon
  },
  {
    value: ShowingWithinOption.Next7Days,
    label: "Next 7 days",
    icon: CalendarCheckOut02Icon
  },
  {
    value: ShowingWithinOption.Next30Days,
    label: "Next 30 days",
    icon: CalendarCheckOut02Icon
  },
  {
    value: ShowingWithinOption.Custom,
    label: "Pick a date range",
    icon: Calendar03Icon,
    isCustom: true
  }
];

interface Props {
  filters?: LeadsFilters;
  setFilters: Dispatch<SetStateAction<LeadsFilters | undefined>>;
  showFilters: boolean;
  setShowFilters: Dispatch<SetStateAction<boolean>>;
  property_ids?: string[];
  unit_ids?: string[];
  search?: string;
  hidePropertyFilter?: boolean;
}

interface FilterOptions {
  statuses?: { status: string; count: number }[];
  properties?: { id: number; name: string; count: number }[];
  units?: { id: number; title: string; property_id: number; count: number }[];
  lead_sources?: {
    id: number;
    name: string;
    source_type: string;
    display_order: number;
    count: number;
  }[];
  lead_types?: { lead_type: string; count: number }[];
  stages?: { id: number; name: string; count: number }[];
  activity_actor_types?: { actor_type: string; count: number }[];
  activity_types?: { activity_type: string; count: number }[];
  actors?: { type: string; id: number | null; name: string; count: number }[];
}

const QUERY = "/portal/v1/leads/filter_options";

export const FiltersDrawer: FC<Props> = ({
  property_ids,
  unit_ids,
  filters,
  setFilters,
  setShowFilters,
  showFilters,
  search,
  hidePropertyFilter
}) => {
  const queryClient = useQueryClient();

  // Extract IDs for API call
  const stageIds = useMemo(
    () => filters?.stages?.map((s) => Number(s.id)),
    [filters?.stages]
  );
  const sourceIds = useMemo(
    () => filters?.leadSources?.map((ls) => Number(ls.id)),
    [filters?.leadSources]
  );
  const filterPropertyIds = useMemo(
    () => filters?.properties?.map((p) => Number(p.id)),
    [filters?.properties]
  );

  const [selectedProperties, setSelectedProperties] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedStages, setSelectedStages] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedLeadSources, setSelectedLeadSources] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedActivityTypes, setSelectedActivityTypes] = useState<string[]>(
    []
  );
  const [selectedCreatedWithin, setSelectedCreatedWithin] =
    useState<CreatedWithinOption>(CreatedWithinOption.None);
  const [selectedCreatedWithinDates, setSelectedCreatedWithinDates] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });
  const [selectedShowingWithin, setSelectedShowingWithin] =
    useState<ShowingWithinOption>(ShowingWithinOption.None);
  const [selectedShowingWithinDates, setSelectedShowingWithinDates] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });

  const { data: filterOptions } = useQuery({
    queryKey: [
      "LEADS_FILTER_OPTIONS",
      {
        property_ids: property_ids || filterPropertyIds,
        unit_ids,
        stage_ids: stageIds,
        source_ids: sourceIds,
        activity_types: filters?.activityTypes,
        search
      }
    ],
    queryFn: () =>
      instance
        .get(
          QUERY +
            "?" +
            queryString.stringify(
              {
                property_ids: property_ids || filterPropertyIds,
                unit_ids,
                stage_ids: stageIds?.length ? stageIds : undefined,
                source_ids: sourceIds?.length ? sourceIds : undefined,
                most_recent_activity: filters?.activityTypes?.length
                  ? { type: filters.activityTypes }
                  : undefined,
                search: search?.trim() || undefined
              },
              { arrayFormat: "brackets" }
            )
        )
        .then(({ data }: { data: FilterOptions }) => data)
  });

  useEffect(() => {
    if (showFilters) {
      queryClient.invalidateQueries({
        queryKey: ["LEADS_FILTER_OPTIONS"]
      });
      setSelectedProperties(filters?.properties || []);
      setSelectedStages(filters?.stages || []);
      setSelectedLeadSources(filters?.leadSources || []);
      setSelectedActivityTypes(filters?.activityTypes || []);
      setSelectedCreatedWithin(
        filters?.createdWithin ?? CreatedWithinOption.None
      );
      setSelectedCreatedWithinDates({
        startDate: filters?.createdAfter ?? "",
        endDate: filters?.createdBefore ?? ""
      });
      setSelectedShowingWithin(
        filters?.showingWithin ?? ShowingWithinOption.None
      );
      setSelectedShowingWithinDates({
        startDate: filters?.showingAfter ?? "",
        endDate: filters?.showingBefore ?? ""
      });
    }
  }, [showFilters, filters, queryClient]);

  const toggleFilter = useCallback(
    (
      filterId: string,
      allOptions: { id: string; name: string }[] | undefined,
      setter: React.Dispatch<
        React.SetStateAction<{ id: string; name: string }[]>
      >
    ) => {
      setter((prev) =>
        prev.some((item) => item.id === filterId)
          ? prev.filter((item) => item.id !== filterId)
          : [
              ...prev,
              {
                id: filterId,
                name: allOptions?.find((opt) => opt.id === filterId)?.name ?? ""
              }
            ]
      );
    },
    []
  );

  const applyFilters = () => {
    setFilters((prev) => ({
      properties: selectedProperties,
      stages: selectedStages,
      leadSources: selectedLeadSources,
      leadTypes: prev?.leadTypes,
      activityTypes: selectedActivityTypes,
      createdWithin: selectedCreatedWithin,
      createdAfter: selectedCreatedWithinDates.startDate || undefined,
      createdBefore: selectedCreatedWithinDates.endDate || undefined,
      showingWithin: selectedShowingWithin,
      showingAfter: selectedShowingWithinDates.startDate || undefined,
      showingBefore: selectedShowingWithinDates.endDate || undefined
    }));
    setShowFilters(false);
  };

  const currentFilters = useMemo(
    () => ({
      properties: selectedProperties,
      stages: selectedStages,
      leadSources: selectedLeadSources,
      activityTypes: selectedActivityTypes
    }),
    [
      selectedProperties,
      selectedStages,
      selectedLeadSources,
      selectedActivityTypes
    ]
  );

  const hasSelectedFilters = useMemo(
    () =>
      Object.values(currentFilters).some(
        (filterArray) => filterArray.length > 0
      ) ||
      selectedCreatedWithin !== CreatedWithinOption.None ||
      selectedShowingWithin !== ShowingWithinOption.None,
    [currentFilters, selectedCreatedWithin, selectedShowingWithin]
  );

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      open={showFilters}
      onClose={() => setShowFilters(false)}
      size={360}
    >
      <div className="flex flex-col h-full px-6">
        <div className="flex items-center justify-between py-4 border-b border-alpha-40">
          <h1 className="text-xl font-bold text-content-1000">Filters</h1>
          <IconButton
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            onClick={() => setShowFilters(false)}
            HugeIcon={Cancel01Icon}
          />
        </div>

        <Show if={hasSelectedFilters}>
          <div className="py-4 border-b border-alpha-40">
            <Button
              variant={ButtonVariant.TEXT}
              className="ml-2 text-base font-bold"
              onClick={() => {
                setSelectedProperties([]);
                setSelectedStages([]);
                setSelectedLeadSources([]);
                setSelectedActivityTypes([]);
                setSelectedCreatedWithin(CreatedWithinOption.None);
                setSelectedCreatedWithinDates({ startDate: "", endDate: "" });
                setSelectedShowingWithin(ShowingWithinOption.None);
                setSelectedShowingWithinDates({ startDate: "", endDate: "" });
              }}
            >
              Unselect all
            </Button>
          </div>
        </Show>

        <Show
          if={!Object.values(filterOptions ?? {}).some((opt) => opt?.length)}
        >
          <p className="my-1 text-sm text-content-400">No options available</p>
        </Show>

        <div className="flex-1 overflow-y-auto divide-y divide-alpha-40">
          <Show
            if={
              !hidePropertyFilter && Boolean(filterOptions?.properties?.length)
            }
          >
            <FilterSection
              title="Property"
              options={
                filterOptions?.properties?.map((p) => ({
                  id: String(p.id),
                  name: p.name,
                  count: p.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedProperties.map(
                (property) => property.id
              )}
              onChange={(propertyId) =>
                toggleFilter(
                  propertyId,
                  filterOptions?.properties?.map((p) => ({
                    id: String(p.id),
                    name: p.name
                  })),
                  setSelectedProperties
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.stages?.length)}>
            <FilterSection
              title="Stage"
              options={
                filterOptions?.stages?.map((s) => ({
                  id: String(s.id),
                  name: s.name,
                  count: s.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedStages.map((stage) => stage.id)}
              onChange={(stageId) =>
                toggleFilter(
                  stageId,
                  filterOptions?.stages?.map((s) => ({
                    id: String(s.id),
                    name: s.name
                  })),
                  setSelectedStages
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.lead_sources?.length)}>
            <FilterSection
              title="Lead Source"
              options={
                filterOptions?.lead_sources?.map((ls) => ({
                  id: String(ls.id),
                  name: ls.name,
                  count: ls.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedLeadSources.map((ls) => ls.id)}
              onChange={(leadSourceId) =>
                toggleFilter(
                  leadSourceId,
                  filterOptions?.lead_sources?.map((ls) => ({
                    id: String(ls.id),
                    name: ls.name
                  })),
                  setSelectedLeadSources
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.activity_types?.length)}>
            <FilterSection
              title="Most recent activity"
              options={
                filterOptions?.activity_types?.map((at) => ({
                  name: at.activity_type,
                  count: at.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedActivityTypes}
              onChange={(activityType) => {
                setSelectedActivityTypes((prev) =>
                  prev.includes(activityType)
                    ? prev.filter((type) => type !== activityType)
                    : [...prev, activityType]
                );
              }}
            />
          </Show>
          <div className="border-t border-alpha-40 py-4 space-y-2">
            <DateRangeSelect
              id="created_within"
              label="Created within"
              value={selectedCreatedWithin}
              startDate={selectedCreatedWithinDates.startDate}
              endDate={selectedCreatedWithinDates.endDate}
              presets={LEADS_DATE_PRESETS}
              onChange={(presetValue, start, end) => {
                setSelectedCreatedWithin(presetValue as CreatedWithinOption);
                setSelectedCreatedWithinDates({
                  startDate: start ?? "",
                  endDate: end ?? ""
                });
              }}
            />
            <DateRangeSelect
              id="showing_within"
              label="Showing date within"
              value={selectedShowingWithin}
              startDate={selectedShowingWithinDates.startDate}
              endDate={selectedShowingWithinDates.endDate}
              presets={SHOWING_DATE_PRESETS}
              onChange={(presetValue, start, end) => {
                setSelectedShowingWithin(presetValue as ShowingWithinOption);
                setSelectedShowingWithinDates({
                  startDate: start ?? "",
                  endDate: end ?? ""
                });
              }}
            />
          </div>
        </div>

        <div className="py-4 flex gap-2 border-t border-alpha-40">
          <Button
            className="w-[152px]"
            size={ButtonSize.BASE}
            onClick={() => setShowFilters(false)}
          >
            Cancel
          </Button>
          <Button
            className="w-[152px]"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
